import React, { useEffect, useState } from "react";
import "@/App.css";
import { AnimatePresence, motion } from "framer-motion";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import { HomeScreen } from "@/screens/HomeScreen";
import { DomainSelect } from "@/screens/DomainSelect";
import { GameScreen } from "@/screens/GameScreen";
import { ResultScreen } from "@/screens/ResultScreen";
import { Leaderboard } from "@/screens/Leaderboard";
import { HowToPlay } from "@/components/HowToPlay";
import { SideBanner, InterstitialAd, RewardedAd, BottomBanner } from "@/components/Ads";
import { AdsProvider } from "@/ads/AdsContext";
import { LoadingSplash } from "@/components/LoadingSplash";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { Store } from "@/screens/Store";
import { LegalScreen } from "@/screens/LegalScreen";
import { LiveHostScreen } from "@/screens/LiveHostScreen";
import { LiveVoterScreen } from "@/screens/LiveVoterScreen";
import { KitScreen } from "@/screens/KitScreen";
import { BlogScreen, BlogArticleScreen } from "@/screens/BlogScreen";
import { InstallPrompt } from "@/components/InstallPrompt";
import { LEGAL_PATHS } from "@/i18n/legal";
import {
  initCrazyGames,
  isCrazyGames,
  loadingStart,
  loadingStop,
  gameplayStart,
  gameplayStop,
  requestRewardedAd,
  requestMidgameAd,
} from "@/lib/crazygames";
import { store } from "@/lib/storage";

const EMBEDDED = typeof window !== "undefined" && window.self !== window.top;

function Game() {
  const [screen, setScreen] = useState("home"); // home | domains | playing | result | leaderboard
  const [pseudo, setPseudo] = useState(() => store.get("de_pseudo") || "");
  const [playerId, setPlayerId] = useState(() => store.get("de_playerId") || null);
  const [domain, setDomain] = useState(null);
  const [result, setResult] = useState(null);
  const [endReason, setEndReason] = useState(null);
  const [luckBonus, setLuckBonus] = useState(0);
  const [gameSeed, setGameSeed] = useState(0);

  const [howTo, setHowTo] = useState(false);
  const [legalTab, setLegalTab] = useState("privacy");
  const [blogSlug, setBlogSlug] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [liveCode, setLiveCode] = useState(null);
  const [interstitial, setInterstitial] = useState(false);
  const [rewarded, setRewarded] = useState(false);
  const [pendingAfterInter, setPendingAfterInter] = useState(null);
  const { t } = useLang();
  const { refresh } = useAuth();
  const [toast, setToast] = useState(null);

  // Retour de paiement Stripe (/payment/success | /payment/cancel)
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith("/payment/success")) {
      const sid = new URLSearchParams(window.location.search).get("session_id");
      setScreen("store");
      let tries = 0;
      const poll = async () => {
        try {
          const st = await api.paymentStatus(sid);
          if (st.payment_status === "paid") {
            await refresh();
            setToast("paymentSuccess");
            history.replaceState(null, "", "/");
            return;
          }
        } catch (e) {
          /* ignore */
        }
        if (tries++ < 8) setTimeout(poll, 1500);
        else {
          setToast("paymentPending");
          history.replaceState(null, "", "/");
        }
      };
      if (sid) poll();
      else history.replaceState(null, "", "/");
    } else if (path.startsWith("/payment/cancel")) {
      setScreen("store");
      setToast("paymentCancel");
      history.replaceState(null, "", "/");
    }
  }, [refresh]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 3800);
    return () => clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    if (pseudo) store.set("de_pseudo", pseudo);
  }, [pseudo]);

  // Initialise le SDK CrazyGames (no-op en standalone sur destinexpress.com)
  useEffect(() => {
    (async () => {
      const ok = await initCrazyGames();
      if (ok) {
        loadingStart();
        loadingStop();
        // Sauvegarde cloud : récupère l'identité synchronisée cross-device
        const savedId = store.get("de_playerId");
        if (savedId) setPlayerId((cur) => cur || savedId);
        const savedPseudo = store.get("de_pseudo");
        if (savedPseudo) setPseudo((cur) => cur || savedPseudo);
      }
    })();
  }, []);

  // Événements gameplay CrazyGames : actif pendant une partie, en pause sinon
  useEffect(() => {
    if (!isCrazyGames()) return;
    if (screen === "playing") gameplayStart();
    else gameplayStop();
  }, [screen]);

  // Accès direct aux pages légales via URL (crawlable pour AdSense/Stripe)
  useEffect(() => {
    const path = window.location.pathname.replace(/\/$/, "");
    if (path === "/kit") {
      setScreen("kit");
      return;
    }
    if (path === "/blog") {
      setScreen("blog");
      return;
    }
    if (path.startsWith("/blog/")) {
      setBlogSlug(path.slice("/blog/".length));
      setScreen("blog_article");
      return;
    }
    if (LEGAL_PATHS[path]) {
      setLegalTab(LEGAL_PATHS[path]);
      setScreen("legal");
    }
  }, []);

  const openLegal = (tab) => {
    setLegalTab(tab);
    setScreen("legal");
    const path = Object.keys(LEGAL_PATHS).find((p) => LEGAL_PATHS[p] === tab) || "/legal";
    history.pushState(null, "", path);
  };

  const closeLegal = () => {
    setScreen("home");
    history.replaceState(null, "", "/");
  };

  // Lien de défi : ?c={gameId} → charge la partie du challenger
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const liveC = params.get("live");
    if (liveC) {
      setLiveCode(liveC.toUpperCase());
      setScreen("live_voter");
      history.replaceState(null, "", "/");
      return;
    }
    const cid = params.get("c");
    if (!cid) return;
    (async () => {
      try {
        const g = await api.getGame(cid);
        setChallenge({ id: cid, pseudo: g.pseudo, score: g.score, domaine: g.domaine });
      } catch (e) {
        /* partie inconnue : on ignore */
      }
      history.replaceState(null, "", "/");
    })();
  }, []);

  const ensurePlayer = async () => {
    if (playerId) return playerId;
    try {
      const p = await api.createPlayer(pseudo.trim());
      setPlayerId(p.id);
      store.set("de_playerId", p.id);
      return p.id;
    } catch (e) {
      return null;
    }
  };

  const startDomains = async () => {
    await ensurePlayer();
    setScreen("domains");
  };

  const selectDomain = (d) => {
    setDomain(d);
    setLuckBonus(0);
    setGameSeed((s) => s + 1);
    setScreen("playing");
  };

  const handleEnd = (res, reason) => {
    setResult(res);
    setEndReason(reason);
    const played = parseInt(store.get("de_gamesPlayed") || "0", 10) + 1;
    store.set("de_gamesPlayed", String(played));
    setScreen("result");
  };

  // Rejouer: pub interstitielle 1 fois sur 2
  const replay = (bonus = 0) => {
    const played = parseInt(store.get("de_gamesPlayed") || "0", 10);
    const proceed = () => {
      setLuckBonus(bonus);
      setGameSeed((s) => s + 1);
      setScreen("playing");
    };
    if (played > 0 && played % 2 === 0) {
      if (isCrazyGames()) {
        // Pub midgame réelle CrazyGames, puis on relance (même en cas d'erreur)
        requestMidgameAd({ onFinished: proceed, onError: proceed });
      } else {
        setPendingAfterInter(() => proceed);
        setInterstitial(true);
      }
    } else {
      proceed();
    }
  };

  // Bonus vidéo récompensé
  const handleBonusReplay = () => {
    if (isCrazyGames()) {
      requestRewardedAd({
        onFinished: () => replay(20),
        // pas de récompense en cas d'erreur/pub non disponible
        onError: () => {},
      });
    } else {
      setRewarded(true);
    }
  };

  const closeInterstitial = () => {
    setInterstitial(false);
    if (pendingAfterInter) {
      pendingAfterInter();
      setPendingAfterInter(null);
    }
  };

  const domainLabel = domain?.nom || "";

  if (screen === "kit") {
    return (
      <KitScreen
        onBack={() => {
          setScreen("home");
          history.replaceState(null, "", "/");
        }}
      />
    );
  }

  if (screen === "blog") {
    return (
      <BlogScreen
        onHome={() => {
          setScreen("home");
          history.replaceState(null, "", "/");
        }}
        onOpen={(slug) => {
          setBlogSlug(slug);
          setScreen("blog_article");
          history.pushState(null, "", `/blog/${slug}`);
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  if (screen === "blog_article") {
    return (
      <BlogArticleScreen
        slug={blogSlug}
        onHome={() => {
          setScreen("home");
          history.replaceState(null, "", "/");
        }}
        onBackToBlog={() => {
          setScreen("blog");
          history.pushState(null, "", "/blog");
          window.scrollTo(0, 0);
        }}
        onPlay={() => {
          setScreen("home");
          history.replaceState(null, "", "/");
        }}
      />
    );
  }

  return (
    <>
      <LoadingSplash />
      {/* Décor plein écran derrière le jeu portrait (rendu premium en iframe large / desktop) */}
      <div aria-hidden className="stage-decor pointer-events-none fixed inset-0 z-0 hidden sm:block" data-testid="stage-decor">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="orb orb-4" />
        <div className="stage-grid" />
      </div>
      {!EMBEDDED && <SideBanner side="left" />}
      {!EMBEDDED && <SideBanner side="right" />}

      <div className="relative z-10 mx-auto min-h-[100dvh] w-full max-w-md sm:shadow-[0_0_120px_10px_rgba(0,0,0,0.55)]">
        <div className="app-bg noise-overlay relative min-h-[100dvh]">
          <AnimatePresence mode="wait">
            <motion.div
              key={screen === "playing" ? `playing-${gameSeed}` : screen}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="relative z-10 min-h-[100dvh] pb-20 xl:pb-0"
            >
              {screen === "home" && (
                <HomeScreen
                  pseudo={pseudo}
                  setPseudo={setPseudo}
                  onPlay={startDomains}
                  onLeaderboard={() => setScreen("leaderboard")}
                  onHowTo={() => setHowTo(true)}
                  onStore={() => setScreen("store")}
                  onLegal={openLegal}
                  onLive={() => setScreen("live_host")}
                  onKit={!EMBEDDED ? () => { setScreen("kit"); history.pushState(null, "", "/kit"); } : null}
                  onBlog={() => { setScreen("blog"); history.pushState(null, "", "/blog"); window.scrollTo(0, 0); }}
                  challenge={challenge}
                />
              )}
              {screen === "live_host" && (
                <LiveHostScreen onExit={() => setScreen("home")} />
              )}
              {screen === "live_voter" && (
                <LiveVoterScreen code={liveCode} onExit={() => { setLiveCode(null); setScreen("home"); }} />
              )}
              {screen === "legal" && (
                <LegalScreen initialTab={legalTab} onBack={closeLegal} />
              )}
              {screen === "store" && <Store onBack={() => setScreen("home")} />}
              {screen === "domains" && (
                <DomainSelect onSelect={selectDomain} onBack={() => setScreen("home")} />
              )}
              {screen === "playing" && domain && (
                <GameScreen
                  key={gameSeed}
                  domain={domain}
                  pseudo={pseudo}
                  playerId={playerId}
                  luckBonus={luckBonus}
                  onEnd={handleEnd}
                  challengeOf={challenge?.id}
                />
              )}
              {screen === "result" && result && (
                <ResultScreen
                  result={result}
                  domainLabel={domainLabel}
                  endReason={endReason}
                  challenge={challenge}
                  onReplay={() => replay(0)}
                  onBonusReplay={handleBonusReplay}
                  onLeaderboard={() => setScreen("leaderboard")}
                />
              )}
              {screen === "leaderboard" && (
                <Leaderboard onBack={() => setScreen(result ? "result" : "home")} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <HowToPlay open={howTo} onClose={() => setHowTo(false)} />
      <InterstitialAd open={interstitial} onClose={closeInterstitial} />
      <RewardedAd
        open={rewarded}
        onClose={() => setRewarded(false)}
        onReward={() => replay(20)}
      />
      {!EMBEDDED && <BottomBanner />}
      {!EMBEDDED && <InstallPrompt />}
      {toast && (
        <div
          data-testid="app-toast"
          className="fixed bottom-24 left-1/2 z-[120] -translate-x-1/2 rounded-full border border-white/15 bg-[#15152a]/95 px-5 py-3 text-sm font-semibold text-white shadow-2xl backdrop-blur-md"
        >
          {t(toast)}
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <AdsProvider>
            <Game />
          </AdsProvider>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
